import { useState } from "react";
import { Link } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import JsonLd from "@/components/feature/JsonLd";
import Breadcrumb from "@/components/feature/Breadcrumb";

const faqs = [
  {
    q: "深瞳档案的内容是从哪里来的？",
    a: "全部来自可公开检索的资料、公开报道与公开表述。我们做的第一步是交叉核对来源，第二步才是整理成时间线。对于无法核实的说法，我们会在文中明确标注，不会当作事实来写。",
  },
  {
    q: "为什么有些内容看起来很“内幕”，读起来却很克制？",
    a: "因为这正是我们想要的写法。情绪最浓的部分往往不是信息量最大的部分。我们更愿意把事件的起点、转折和后续讲清楚，让你自己得出结论，而不是替你激动。",
  },
  {
    q: "文中出现的事件和人物是真实存在的吗？",
    a: "档案中的具体叙述是对公开信息与常见现象的整理与解读，部分案例做了概括与匿名处理。我们不对任何未公开、未经核实的事项作事实认定，也不针对特定个人作出评价。",
  },
  {
    q: "我可以提供线索吗？",
    a: "欢迎。你可以通过联系表单提交线索，尽量附上可以核对的信息来源。我们会先做核对，确认可用后才会进入整理流程，也不会公开任何未经你同意的个人身份信息。",
  },
  {
    q: "如果发现内容有误怎么办？",
    a: "请通过联系表单或邮箱告诉我们具体的段落与依据。核实后我们会更新原文，并在文末标注修订时间与修订内容，而不是悄悄替换。",
  },
  {
    q: "网站会收费或者需要注册吗？",
    a: "目前所有档案都可以免费阅读，不需要注册。我们也没有付费会员或内容订阅计划。",
  },
  {
    q: "多久更新一次？",
    a: "通常是每周更新一到三篇。长档案需要更长的核对时间，所以节奏会有波动。你可以在首页看到最新的收录状态。",
  },
  {
    q: "本站的观点是否代表某种立场？",
    a: "本站不隶属于任何机构，也不代表任何一方的立场。所有解读都是编辑团队基于公开信息的观察角度，欢迎不同意见，也欢迎指正。",
  },
];

export default function FaqPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  useSeo({
    title: "常见问题 | 深瞳档案 内容来源与使用说明",
    description:
      "关于深瞳档案的常见问题：内容来源、写作方式、线索提交、内容更正与更新频率等。一次性了解本站的内容原则与使用说明。",
    keywords: "常见问题,内容来源,线索提交,内容更正,深瞳档案",
    path: "/faq",
    type: "website",
  });

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://www.shentongdangan.cn/" },
        { "@type": "ListItem", position: 2, name: "常见问题", item: "https://www.shentongdangan.cn/faq" },
      ],
    },
  ];

  return (
    <>
      <JsonLd data={schema} />
      <div className="border-b border-background-200 bg-background-100">
        <div className="container-page">
          <Breadcrumb items={[{ label: "首页", path: "/" }, { label: "常见问题" }]} />
          <div className="pb-12">
            <span className="text-xs font-medium tracking-[0.2em] text-primary-600">FAQ</span>
            <h1 className="mt-3 font-heading text-3xl font-bold text-foreground-950 md:text-5xl">常见问题</h1>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
              关于内容来源、写作方式与使用说明，这里集中回答了读者最常问的几个问题。
              如果没有找到答案，欢迎直接联系我们。
            </p>
          </div>
        </div>
      </div>

      <div className="container-page grid grid-cols-1 gap-10 py-12 lg:grid-cols-[1fr_280px]">
        <div>
          <ul className="flex flex-col gap-3">
            {faqs.map((item, index) => {
              const isOpen = openIndex === index;
              return (
                <li key={item.q} className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
                  <h2>
                    <button
                      type="button"
                      onClick={() => setOpenIndex(isOpen ? null : index)}
                      aria-expanded={isOpen}
                      className="flex w-full cursor-pointer items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-background-100"
                    >
                      <span className="font-heading text-sm font-bold text-foreground-950 md:text-base">
                        {item.q}
                      </span>
                      <i
                        className={`shrink-0 text-lg text-primary-600 transition-transform ${
                          isOpen ? "ri-subtract-line" : "ri-add-line"
                        }`}
                        aria-hidden="true"
                      />
                    </button>
                  </h2>
                  {isOpen && (
                    <div className="border-t border-background-200 px-5 py-4">
                      <p className="text-sm leading-relaxed text-foreground-600">{item.a}</p>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        <aside className="flex flex-col gap-5">
          <div className="rounded-lg border border-background-200 bg-background-100 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">还有疑问？</h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              如果上面的回答没有覆盖你的问题，可以直接给我们留言，我们会在工作时间内回复。
            </p>
            <Link
              to="/contact"
              className="mt-4 flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              联系我们
              <i className="ri-arrow-right-line" aria-hidden="true" />
            </Link>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-100 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">热门栏目</h2>
            <ul className="mt-3 space-y-2.5">
              {[
                { name: "行业内幕", slug: "industry" },
                { name: "网络迷雾", slug: "internet" },
                { name: "商业暗流", slug: "business" },
              ].map((c) => (
                <li key={c.slug}>
                  <Link
                    to={`/channel/${c.slug}`}
                    className="flex items-center gap-2 text-sm text-foreground-600 transition-colors hover:text-primary-700"
                  >
                    <i className="ri-arrow-right-s-line text-primary-500" aria-hidden="true" />
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </>
  );
}